import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from "@reduxjs/toolkit";

const initialState = {
  serviceList: {
    preist: {},       // { eventID: { providerID, ... } }
    catering: {},     // { eventID: [{ providerID, mapping_id, ... }] }
    tenthouse: {},    // { eventID: { providerID, ... } }
    venue: {}         // { eventID: { providerID, spaceId, ... } }
  },
  syncing: false,
  syncError: null,
  refreshKey: 0 
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const { 
        serviceType, 
        eventID, 
        providerID, 
        service_id,
        price,
        discountprice,
        discountvalidupto,
        quantity, 
        mapping_id,
        cart_id,
        is_checked_out,
        package_id,
        // Venue specific fields
        spaceId,
        spaceName,
        guestCount,
        bookingDate,
        bookingTime,
        venueDetails
      } = action.payload;

      // Validation
      if (!serviceType || !eventID || !providerID) {
        console.warn('Missing required fields');
        return;
      }

      // Initialize service type if not exists
      if (!state.serviceList[serviceType]) {
        state.serviceList[serviceType] = {};
      }

      // Handle Venue (single provider per event with space selection)
      if (serviceType === 'venue') {
        // Check if there's already a venue for this event
        const existing = state.serviceList.venue[eventID];

        if (existing) {
          console.warn(`Replacing venue for event ${eventID}. Provider ${existing.providerID} -> ${providerID}`);
        }

        state.serviceList.venue[eventID] = {
          providerID,
          service_id,
          cart_id,
          price,
          discountprice,
          discountvalidupto,
          quantity: quantity || guestCount || 1,
          spaceId: spaceId || null,
          spaceName: spaceName || null,
          guestCount: guestCount || quantity || null,
          bookingDate: bookingDate || null,
          bookingTime: bookingTime || null,
          venueDetails: venueDetails || null,
          is_checked_out: is_checked_out || false
        };
      }
      // Handle Priest
      else if (serviceType === 'preist') {
        // Check if there's already a priest for this event
        const existing = state.serviceList.preist[eventID];

        if (existing) {                      
          console.warn(`Replacing priest for event ${eventID}. Provider ${existing.providerID} -> ${providerID}`);
        }
        
        state.serviceList.preist[eventID] = {
          providerID,
          service_id,
          cart_id,
          price,
          discountprice,
          discountvalidupto,
          quantity: quantity || 1,
          package_id: package_id || null,
          is_checked_out: is_checked_out || false
        };
      }
      // Handle Catering (array-based)
      else if (serviceType === 'catering') {
        if (!state.serviceList.catering[eventID]) {
          state.serviceList.catering[eventID] = [];
        }

        // Check if provider+service+mapping already exists
        const exists = state.serviceList.catering[eventID].some(item => 
          item.providerID === providerID && 
          (mapping_id ? item.mapping_id === mapping_id : true)
        );

        if (!exists) {
          state.serviceList.catering[eventID].push({
            providerID,
            service_id,
            mapping_id,
            cart_id,
            price,
            discountprice,
            discountvalidupto,
            quantity: quantity || 1,
            is_checked_out: is_checked_out || false
          });
        }
      }
      // Handle Tenthouse (single provider per event)
      else if (serviceType === 'tenthouse') {
        const existing = state.serviceList.tenthouse[eventID];
        
        // Prevent duplicate providers
        if (existing?.providerID === providerID) {
          console.warn(`Provider ${providerID} already exists for ${serviceType}`);
          return;
        }

        state.serviceList.tenthouse[eventID] = {
          providerID,
          service_id,
          cart_id,
          price,
          discountprice,
          discountvalidupto,
          quantity: quantity || 1,
          mapping_id: mapping_id || null,
          is_checked_out: is_checked_out || false
        };
      }
    },

    triggerRefresh: (state) => {
      state.refreshKey += 1;
    },

    removeFromCart: (state, action) => {
      const { serviceType, eventID, providerID, mapping_id, spaceId } = action.payload;

      if (!state.serviceList[serviceType]?.[eventID]) return;

      // Handle Venue
      if (serviceType === 'venue') {
        if (state.serviceList.venue[eventID]?.providerID === providerID) {
          delete state.serviceList.venue[eventID];
        }
      }
      // Handle Catering (array-based)
      else if (serviceType === 'catering') {
        const cateringItems = state.serviceList.catering[eventID];

        const itemsArray = Array.isArray(cateringItems)
          ? cateringItems
          : [cateringItems].filter(Boolean);

        // Filter items using both providerID and mapping_id
        state.serviceList.catering[eventID] = itemsArray.filter(item =>
          !(item.providerID === providerID && item.mapping_id === mapping_id)
        );

        // Cleanup empty arrays
        if (state.serviceList.catering[eventID].length === 0) {
          delete state.serviceList.catering[eventID];
        }
      } 
      // Handle Priest & Tenthouse
      else {
        if (state.serviceList[serviceType][eventID]?.providerID === providerID) {
          delete state.serviceList[serviceType][eventID];
        }
      }
    },

    removePreistByEventId: (state, action) => {
      const { eventID } = action.payload;
      if (state.serviceList.preist[eventID]) {
        delete state.serviceList.preist[eventID];
      }
    },

    removeTenthouseByEventId: (state, action) => {
      const { eventID } = action.payload;
      if (state.serviceList.tenthouse[eventID]) {
        delete state.serviceList.tenthouse[eventID];
      }
    },

    removeVenueByEventId: (state, action) => {
      const { eventID } = action.payload;
      if (state.serviceList.venue[eventID]) {
        delete state.serviceList.venue[eventID];
      }
    },

    clearCart: (state) => {
      state.serviceList = { preist: {}, catering: {}, tenthouse: {}, venue: {} };
    },
    
    clearPreistCart: (state) => {
      state.serviceList.preist = {};
    },
    
    clearTenthouseCart: (state) => {
      state.serviceList.tenthouse = {};
    },

    clearCateringCart: (state) => {
      state.serviceList.catering = {};
    },

    clearVenueCart: (state) => {
      state.serviceList.venue = {};
    },

    updateVenueBookingDetails: (state, action) => {
      const { eventID, providerID, spaceId, guestCount, bookingDate, bookingTime } = action.payload;
      
      if (state.serviceList.venue[eventID]?.providerID === providerID) {
        state.serviceList.venue[eventID] = {
          ...state.serviceList.venue[eventID],
          spaceId: spaceId || state.serviceList.venue[eventID].spaceId,
          guestCount: guestCount || state.serviceList.venue[eventID].guestCount,
          bookingDate: bookingDate || state.serviceList.venue[eventID].bookingDate,
          bookingTime: bookingTime || state.serviceList.venue[eventID].bookingTime,
        };
      }
    }
  }
});

// ============================================
// SELECTORS
// ============================================

// Check if provider is added (for display purposes)
export const isProviderAdded = (state, eventID, providerID, serviceID, mapping_id = null) => {
  const serviceMap = {
    7: 'preist',
    8: 'tenthouse',
    9: 'catering',
    10: 'venue'
  };

  const serviceType = serviceMap[serviceID];
  if (!serviceType) return false;
  
  const serviceData = state.cart.serviceList[serviceType];
  if (!serviceData || !serviceData[eventID]) return false;

  // Venue
  if (serviceType === 'venue') {
    return serviceData[eventID]?.providerID === providerID;
  }
  // Catering
  else if (serviceType === 'catering') {
    const itemsArray = Array.isArray(serviceData[eventID]) 
      ? serviceData[eventID] 
      : [serviceData[eventID]].filter(Boolean);
    
    return itemsArray.some(item =>
      item.providerID === providerID && 
      (mapping_id ? item.mapping_id === mapping_id : true)
    );
  } 
  // Priest & Tenthouse
  else {
    const providerData = serviceData[eventID];
    if (!providerData) return false;
    return providerData.providerID === providerID;
  }
};

// Check if any provider of a service type exists for an event
export const checkisProviderAdded = (state, eventID, providerID, serviceID, mapping_id = null) => {
  const serviceMap = {
    7: 'preist',
    8: 'tenthouse',
    9: 'catering',
    10: 'venue'
  };

  const serviceType = serviceMap[serviceID];
  if (!serviceType) return false;
  
  const serviceData = state.cart.serviceList[serviceType];
  if (!serviceData || !serviceData[eventID]) return false;

  // Venue
  if (serviceType === 'venue') {
    return Boolean(serviceData[eventID]);
  }
  // Catering
  else if (serviceType === 'catering') {
    const itemsArray = Array.isArray(serviceData[eventID]) 
      ? serviceData[eventID] 
      : [serviceData[eventID]].filter(Boolean);
    
    return itemsArray.some(item => 
      item.providerID === providerID && 
      (mapping_id ? item.mapping_id === mapping_id : true)
    );
  } 
  // Priest & Tenthouse
  else {
    return Boolean(serviceData[eventID]);
  }
};

// Check if provider is added for event tabs (simplified)
export const isProviderAddedForEventTabs = (state, eventID, serviceID) => {
  const serviceMap = {
    7: 'preist',
    8: 'tenthouse',
    9: 'catering',
    10: 'venue'
  };

  const serviceType = serviceMap[serviceID];
  if (!serviceType) return false;

  const serviceData = state.cart.serviceList[serviceType];
  if (!serviceData) return false;

  // Catering = array, others = object
  if (serviceType === "catering") {
    return Array.isArray(serviceData[eventID]) && serviceData[eventID].length > 0;
  } else {
    return Boolean(serviceData[eventID]);
  }
};

// Get cart ID for a service
export const getCartId = (state, eventID, serviceID, mapping_id = null) => {
  const serviceMap = {
    7: 'preist',
    8: 'tenthouse',
    9: 'catering',
    10: 'venue'
  };

  const serviceType = serviceMap[serviceID];
  if (!serviceType) return null;

  const serviceData = state.cart.serviceList[serviceType];
  if (!serviceData || !serviceData[eventID]) return null;

  // Venue
  if (serviceType === 'venue') {
    return serviceData[eventID]?.cart_id || null;
  }
  // Catering
  else if (serviceType === 'catering') {
    const itemsArray = Array.isArray(serviceData[eventID]) 
      ? serviceData[eventID] 
      : [serviceData[eventID]].filter(Boolean);
    
    const item = mapping_id 
      ? itemsArray.find(item => item.mapping_id === mapping_id)
      : itemsArray[0];
      
    return item?.cart_id;
  } 
  // Priest & Tenthouse
  else {
    return serviceData[eventID]?.cart_id || null;
  }
};

// Get all cart IDs for an event
export const getCartIdsForEvent = (state, eventID) => {
  const result = {
    preist: null,
    tenthouse: null,
    catering: [],
    venue: null
  };

  // Priest
  if (state.cart.serviceList.preist[eventID]) {
    result.preist = state.cart.serviceList.preist[eventID].cart_id;
  }

  // Tenthouse
  if (state.cart.serviceList.tenthouse[eventID]) {
    result.tenthouse = state.cart.serviceList.tenthouse[eventID].cart_id;
  }

  // Venue
  if (state.cart.serviceList.venue[eventID]) {
    result.venue = state.cart.serviceList.venue[eventID].cart_id;
  }

  // Catering
  if (state.cart.serviceList.catering[eventID]) {
    const items = Array.isArray(state.cart.serviceList.catering[eventID])
      ? state.cart.serviceList.catering[eventID]
      : [state.cart.serviceList.catering[eventID]].filter(Boolean);
    
    result.catering = items.map(item => item.cart_id).filter(Boolean);
  }

  return result;
};

// Get venue provider ID for an event
export const getVenueProviderID = (state, eventID) => {
  const venueData = state.cart.serviceList.venue[eventID];
  return venueData?.providerID || null;
};

// Get venue details for an event
export const getVenueDetails = (state, eventID) => {
  const venueData = state.cart.serviceList.venue[eventID];
  if (!venueData) return null;
  
  return {
    providerID: venueData.providerID,
    spaceId: venueData.spaceId,
    spaceName: venueData.spaceName,
    guestCount: venueData.guestCount,
    bookingDate: venueData.bookingDate,
    bookingTime: venueData.bookingTime,
    price: venueData.price,
    discountprice: venueData.discountprice,
    venueDetails: venueData.venueDetails,
    is_checked_out: venueData.is_checked_out
  };
};

// Get tenthouse provider ID for an event
export const getTenthouseProviderID = (state, eventID) => {
  const tenthouseData = state.cart.serviceList.tenthouse[eventID];
  return tenthouseData?.providerID || null;
};

// Get all catering items for an event
export const getCateringItemsByEventId = (state, eventID) => {
  const cateringData = state.cart.serviceList.catering?.[eventID];
  if (!cateringData) return [];

  return Array.isArray(cateringData) ? cateringData : [cateringData];
};

// Get all service providers for an event (simplified)
export const getEventServiceProviders = (state, eventID) => {
  const result = {
    preist: null,
    tenthouse: null,
    catering: [],
    venue: null
  };

  // Priest
  if (state.cart.serviceList.preist[eventID]) {
    result.preist = state.cart.serviceList.preist[eventID];
  }

  // Tenthouse
  if (state.cart.serviceList.tenthouse[eventID]) {
    result.tenthouse = state.cart.serviceList.tenthouse[eventID];
  }

  // Venue
  if (state.cart.serviceList.venue[eventID]) {
    result.venue = state.cart.serviceList.venue[eventID];
  }

  // Catering
  if (state.cart.serviceList.catering[eventID]) {
    const items = Array.isArray(state.cart.serviceList.catering[eventID])
      ? state.cart.serviceList.catering[eventID]
      : [state.cart.serviceList.catering[eventID]].filter(Boolean);
    
    result.catering = items;
  }

  return result;
};

// Check if a service is checked out
export const isServiceCheckedOut = (state, eventID, serviceID, mapping_id = null) => {
  const serviceMap = {
    7: 'preist',
    8: 'tenthouse',
    9: 'catering',
    10: 'venue'
  };

  const serviceType = serviceMap[serviceID];
  if (!serviceType) return false;

  const serviceData = state.cart.serviceList[serviceType];
  if (!serviceData || !serviceData[eventID]) return false;

  // Venue
  if (serviceType === 'venue') {
    return Boolean(serviceData[eventID]?.is_checked_out);
  }
  // Catering
  else if (serviceType === 'catering') {
    const itemsArray = Array.isArray(serviceData[eventID])
      ? serviceData[eventID]
      : [serviceData[eventID]].filter(Boolean);

    const item = mapping_id
      ? itemsArray.find(i => i.mapping_id === mapping_id)
      : itemsArray[0];

    return Boolean(item?.is_checked_out);
  } 
  // Priest & Tenthouse
  else {
    return Boolean(serviceData[eventID]?.is_checked_out);
  }
};

// Get total cart items count for an event
export const getEventCartCount = (state, eventID) => {
  let count = 0;

  // Priest
  if (state.cart.serviceList.preist[eventID]) count++;

  // Tenthouse
  if (state.cart.serviceList.tenthouse[eventID]) count++;

  // Venue
  if (state.cart.serviceList.venue[eventID]) count++;

  // Catering
  if (state.cart.serviceList.catering[eventID]) {
    const items = Array.isArray(state.cart.serviceList.catering[eventID])
      ? state.cart.serviceList.catering[eventID]
      : [state.cart.serviceList.catering[eventID]].filter(Boolean);
    count += items.length;
  }

  return count;
};

// Create selector for event service identifiers
export const makeGetEventServiceIdentifiers = () =>
  createSelector(
    [(state) => state.cart.serviceList, (_, eventID) => eventID],
    (serviceList, eventID) => {
      const preist = serviceList.preist?.[eventID] || {};
      const tenthouse = serviceList.tenthouse?.[eventID] || {};
      const venue = serviceList.venue?.[eventID] || {};
      const catering = serviceList.catering?.[eventID] || [];

      return {
        priestCartId: preist.cart_id || null,
        priestProviderId: preist.providerID || null,
        tenthouseCartId: tenthouse.cart_id || null,
        tenthouseProviderId: tenthouse.providerID || null,
        venueCartId: venue.cart_id || null,
        venueProviderId: venue.providerID || null,
        venueSpaceId: venue.spaceId || null,
        catering: Array.isArray(catering)
          ? catering.map(c => ({
              mappingId: c.mapping_id,
              providerId: c.providerID,
              cartId: c.cart_id,
            }))
          : [],
      };
    }
  );

// Check if any service is checked out for an event
export const isAnyServiceCheckedOut = (state, eventID) => {
  const services = getEventServiceProviders(state, eventID);
  
  return (
    services.preist?.is_checked_out ||
    services.tenthouse?.is_checked_out ||
    services.venue?.is_checked_out ||
    services.catering.some(item => item.is_checked_out)
  );
};

// ============================================
// ACTIONS
// ============================================

export const { 
  addToCart, 
  removeFromCart, 
  clearCart,
  clearPreistCart,
  clearTenthouseCart,
  clearCateringCart,
  clearVenueCart,
  triggerRefresh,
  removePreistByEventId,
  removeTenthouseByEventId,
  removeVenueByEventId,
  updateVenueBookingDetails,
} = cartSlice.actions;

export default cartSlice.reducer;
export const selectCartData = (state) => state.cart.items;